import React, { useState, useEffect, useRef } from 'react';
import { FaUsers, FaCalendarAlt, FaTicketAlt, FaStar, FaHeart, FaQrcode } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { useNotifications } from '../context/NotificationContext';
import { supabase } from '../services/supabase';
import { avatarUrl as av } from '../utils/imageUrl';
import StatCard from '../components/dashboard/StatCard';
import '../styles/DashboardPage.css';

const DashboardPage = () => {
  const { business } = useAuth();
  const { activeLocation, locations } = useLocation();

  const [stats, setStats]               = useState({ followers: 0, bookings: 0, scans: 0, couponsUsed: 0, rating: 0 });
  const [todayStats, setTodayStats]     = useState({ reservations: 0, scans: 0, accepted: 0 });
  const [pendingBookings, setPendingBookings] = useState([]);
  const [followers, setFollowers]       = useState([]);
  const [recentScans, setRecentScans]   = useState([]);
  const [recentCoupons, setRecentCoupons] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [tab, setTab]                   = useState('activity');
  const [actionLoading, setActionLoading] = useState(null);
  const { setPendingCount } = useNotifications();

  const [bookingsEnabled, setBookingsEnabled] = useState(true);
  const [closedUntil, setClosedUntil]         = useState(null);
  const [showCloseModal, setShowCloseModal]   = useState(false);
  const [timeInputVal, setTimeInputVal]       = useState('');
  const [bookingToggleLoading, setBookingToggleLoading] = useState(false);

  const [floorPlanEnabled, setFloorPlanEnabled] = useState(true);
  const [floorPlanLoading, setFloorPlanLoading] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (business?.id) loadAll();
  }, [business?.id, activeLocation?.id]);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadStats(), loadTodayStats(), loadPendingBookings(), loadFollowers(), loadScans(), loadCouponLogs(), loadBookingStatus(), loadFloorPlanStatus()]);
    setLoading(false);
  };

  const loadBookingStatus = async () => {
    if (!activeLocation?.id) return;
    const { data } = await supabase
      .from('business_locations')
      .select('bookings_enabled, bookings_closed_until')
      .eq('id', activeLocation.id)
      .single();
    if (data) {
      const stillClosed = data.bookings_closed_until && new Date(data.bookings_closed_until) > new Date();
      setBookingsEnabled(data.bookings_enabled !== false && !stillClosed);
      setClosedUntil(stillClosed ? data.bookings_closed_until : null);
    }
  };

  const handleOpenBookings = async () => {
    if (!activeLocation?.id) return;
    setBookingToggleLoading(true);
    await supabase.from('business_locations')
      .update({ bookings_enabled: true, bookings_closed_until: null })
      .eq('id', activeLocation.id);
    setBookingsEnabled(true);
    setClosedUntil(null);
    setBookingToggleLoading(false);
  };

  const loadFloorPlanStatus = async () => {
    if (!activeLocation?.id) return;
    const { data } = await supabase
      .from('business_locations')
      .select('floor_plan_enabled')
      .eq('id', activeLocation.id)
      .single();
    if (data) setFloorPlanEnabled(data.floor_plan_enabled !== false);
  };

  const handleToggleFloorPlan = async () => {
    if (!activeLocation?.id) return;
    setFloorPlanLoading(true);
    const newVal = !floorPlanEnabled;
    await supabase.from('business_locations')
      .update({ floor_plan_enabled: newVal })
      .eq('id', activeLocation.id);
    setFloorPlanEnabled(newVal);
    setFloorPlanLoading(false);
  };

  const handleCloseBookings = async (closedUntilVal) => {
    if (!activeLocation?.id) return;
    setBookingToggleLoading(true);
    setShowCloseModal(false);
    await supabase.from('business_locations')
      .update({ bookings_enabled: false, bookings_closed_until: closedUntilVal || null })
      .eq('id', activeLocation.id);
    setBookingsEnabled(false);
    setClosedUntil(closedUntilVal || null);
    setTimeInputVal('');
    setBookingToggleLoading(false);
  };

  const loadStats = async () => {
    try {
      const [bookingsRes, scanRes, couponRes, ratingRes] = await Promise.all([
        supabase.from('flash_bookings').select('id', { count: 'exact' }).eq('business_id', business.id).eq('status', 'pending'),
        supabase.from('point_logs').select('id', { count: 'exact' }).eq('business_id', business.id),
        supabase.from('coupon_logs').select('id', { count: 'exact' }).eq('business_id', business.id),
        supabase.from('reviews').select('rating').eq('business_id', business.id),
      ]);
      const avg = ratingRes.data?.length
        ? (ratingRes.data.reduce((s, r) => s + r.rating, 0) / ratingRes.data.length).toFixed(1)
        : '—';
      setStats(prev => ({
        ...prev,
        bookings:    bookingsRes.data?.length || 0,
        scans:       scanRes.data?.length || 0,
        couponsUsed: couponRes.data?.length || 0,
        rating:      avg,
      }));
    } catch (e) { console.error('loadStats:', e); }
  };

  const loadTodayStats = async () => {
    try {
      const todayStart = today + 'T00:00:00';
      const todayEnd   = today + 'T23:59:59';

      const [rezRes, scanRes, acceptRes] = await Promise.all([
        supabase.from('flash_bookings').select('id', { count: 'exact' })
          .eq('business_id', business.id).eq('reservation_date', today),
        supabase.from('point_logs').select('id', { count: 'exact' })
          .eq('business_id', business.id)
          .gte('created_at', todayStart).lte('created_at', todayEnd),
        supabase.from('flash_bookings').select('id', { count: 'exact' })
          .eq('business_id', business.id).eq('reservation_date', today).eq('status', 'accepted'),
      ]);

      setTodayStats({
        reservations: rezRes.data?.length || 0,
        scans:        scanRes.data?.length || 0,
        accepted:     acceptRes.data?.length || 0,
      });
    } catch (e) { console.error('loadTodayStats:', e); }
  };

  const loadPendingBookings = async () => {
    try {
      let q = supabase.from('flash_bookings')
        .select('*, users(id, full_name, avatar_url)')
        .eq('business_id', business.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: true })
        .limit(10);
      if (activeLocation?.id) q = q.or(`location_id.eq.${activeLocation.id},location_id.is.null`);
      const { data } = await q;
      setPendingBookings(data || []);
      setStats(prev => ({ ...prev, bookings: data?.length || 0 }));
    } catch (e) { console.error('loadPendingBookings:', e); }
  };

  const sendPushToUser = async (userId, title, body, data = {}) => {
    if (!userId) return;
    await supabase.rpc('notify_user', {
      p_user_id: userId, p_title: title, p_body: body, p_data: data,
    });
  };

  const handleAccept = async (booking) => {
    setActionLoading(booking.id);
    const { error } = await supabase.from('flash_bookings')
      .update({ status: 'accepted' }).eq('id', booking.id);
    if (!error) {
      setPendingBookings(prev => prev.filter(b => b.id !== booking.id));
      setStats(prev => ({ ...prev, bookings: Math.max(0, prev.bookings - 1) }));
      setTodayStats(prev => ({ ...prev, accepted: prev.accepted + 1 }));
      setPendingCount(c => Math.max(0, c - 1));
      if (booking.table_id && booking.reservation_date === today) {
        await supabase.from('tables').update({ status: 'reserved' }).eq('id', booking.table_id);
      }
      if (booking.user_id) {
        const date = booking.reservation_date || '';
        const time = booking.reservation_time?.slice(0, 5) || '';
        const pushTitle = '✅ Rezervasiya Təsdiqləndi!';
        const pushBody  = `${business.name} — ${date} saat ${time}`;
        await sendPushToUser(booking.user_id, pushTitle, pushBody, { type: 'reservation_confirmed', booking_id: booking.id, business_id: business.id });
      }
    }
    setActionLoading(null);
  };

  const handleReject = async (booking) => {
    setActionLoading(booking.id);
    const { error } = await supabase.from('flash_bookings')
      .update({ status: 'rejected' }).eq('id', booking.id);
    if (!error) {
      setPendingBookings(prev => prev.filter(b => b.id !== booking.id));
      setStats(prev => ({ ...prev, bookings: Math.max(0, prev.bookings - 1) }));
      setPendingCount(c => Math.max(0, c - 1));
      if (booking.table_id) {
        await supabase.from('tables').update({ status: 'available' }).eq('id', booking.table_id);
      }
      if (booking.user_id) {
        const pushTitle = '❌ Rezervasiya Rədd Edildi';
        const pushBody  = `${business.name}`;
        await sendPushToUser(booking.user_id, pushTitle, pushBody, { type: 'reservation_rejected', booking_id: booking.id, business_id: business.id });
      }
    }
    setActionLoading(null);
  };

  const loadFollowers = async () => {
    if (!business?.id) return;
    let q = supabase.from('business_follows')
      .select('created_at, user_id, location_id')
      .eq('business_id', business.id)
      .order('created_at', { ascending: false }).limit(20);
    if (activeLocation?.id) q = q.eq('location_id', activeLocation.id);
    let { data, error } = await q;
    if (error) {
      ({ data, error } = await supabase.from('business_follows')
        .select('created_at, user_id')
        .eq('business_id', business.id)
        .order('created_at', { ascending: false }).limit(20));
    }
    if (!data?.length) { setFollowers([]); return; }
    const userIds = data.map(f => f.user_id);
    const { data: usersData } = await supabase.from('public_user_profiles')
      .select('id, full_name, username, avatar_url').in('id', userIds);
    const usersMap = Object.fromEntries((usersData || []).map(u => [u.id, u]));
    const locsMap  = Object.fromEntries((locations || []).map(l => [l.id, l]));
    setFollowers(data.map(f => ({
      ...f,
      users:    usersMap[f.user_id] || null,
      location: f.location_id ? (locsMap[f.location_id] || null) : null,
    })));
    setStats(prev => ({ ...prev, followers: data.length }));
  };

  const loadScans = async () => {
    const { data } = await supabase.from('point_logs').select('*')
      .eq('business_id', business.id)
      .order('created_at', { ascending: false }).limit(15);
    setRecentScans(data || []);
  };

  const loadCouponLogs = async () => {
    const { data, error } = await supabase.rpc('get_coupon_logs', { p_business_id: business.id });
    if (error) console.error('coupon_logs error:', error.message);
    setRecentCoupons(data || []);
  };

  const timeAgo = (ts) => {
    const diff = Date.now() - new Date(ts);
    const m = Math.floor(diff / 60000);
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(diff / 86400000);
    if (m < 1)  return 'İndicə';
    if (m < 60) return `${m} dəq əvvəl`;
    if (h < 24) return `${h} saat əvvəl`;
    return `${d} gün əvvəl`;
  };

  const TIER_COLOR = { '5-20': '#3B82F6', '20-50': '#F59E0B', '50+': '#10B981', 'signup_bonus': '#8B5CF6' };
  const TIER_LABEL = { '5-20': '5–20 ₼ (+10 xal)', '20-50': '20–50 ₼ (+35 xal)', '50+': '50+ ₼ (+80 xal)', 'signup_bonus': '🎁 Qeydiyyat Bonusu' };

  if (loading) return <div className="dashboard-loading"><h2>Yüklənir...</h2></div>;

  return (
    <div className="dashboard-page">

      <div className="dashboard-header">
        <h1>Xoş Gəldiniz, {business?.name}! 👋</h1>
        <p>Biznesinizin canlı statistikası</p>
      </div>

      {/* ── REZERVASİYA STATUS ── */}
      {activeLocation?.id && (
        <div style={{
          background: '#fff', borderRadius: 18, padding: '16px 20px', marginBottom: 20,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: `1.5px solid ${bookingsEnabled ? '#D1FAE5' : '#FEE2E2'}`,
          display: 'flex', alignItems: 'center', gap: 16,
        }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12, flexShrink: 0,
            background: bookingsEnabled ? '#ECFDF5' : '#FEF2F2',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
          }}>
            {bookingsEnabled ? '📅' : '🔒'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>Rezervasiya Statusu</div>
            <div style={{ fontSize: 12, marginTop: 2, color: bookingsEnabled ? '#10B981' : '#EF4444', fontWeight: 600 }}>
              {bookingsEnabled
                ? '● Açıqdır — Müştərilər rezervasiya edə bilər'
                : closedUntil
                  ? `● Bağlıdır — saat ${new Date(closedUntil).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}-a qədər`
                  : '● Bağlıdır — Müştərilər rezervasiya edə bilməz'}
            </div>
          </div>
          <button
            onClick={bookingsEnabled ? () => setShowCloseModal(true) : handleOpenBookings}
            disabled={bookingToggleLoading}
            style={{
              padding: '10px 20px', borderRadius: 10, border: 'none', flexShrink: 0,
              background: bookingsEnabled ? '#EF4444' : '#10B981',
              color: '#fff', fontWeight: 700, fontSize: 13,
              cursor: bookingToggleLoading ? 'not-allowed' : 'pointer',
              opacity: bookingToggleLoading ? 0.6 : 1,
            }}
          >
            {bookingToggleLoading ? '...' : bookingsEnabled ? '🔒 Bağla' : '🔓 Aç'}
          </button>
        </div>
      )}

      {/* ── FLOOR PLAN STATUS ── */}
      {activeLocation?.id && (
        <div style={{
          background: '#fff', borderRadius: 18, padding: '16px 20px', marginBottom: 20,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: `1.5px solid ${floorPlanEnabled ? '#DBEAFE' : '#E5E7EB'}`,
          display: 'flex', alignItems: 'center', gap: 16,
        }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12, flexShrink: 0,
            background: floorPlanEnabled ? '#EFF6FF' : '#F1F5F9',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
          }}>
            🗺️
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>Yer Planı (Floor Plan)</div>
            <div style={{ fontSize: 12, marginTop: 2, color: floorPlanEnabled ? '#3B82F6' : '#94A3B8', fontWeight: 600 }}>
              {floorPlanEnabled
                ? '● Aktiv — Mobil tətbiqdə görünür'
                : '● Deaktiv — Mobil tətbiqdə gizlididir'}
            </div>
          </div>
          <button
            onClick={handleToggleFloorPlan}
            disabled={floorPlanLoading}
            style={{
              padding: '10px 20px', borderRadius: 10, border: 'none', flexShrink: 0,
              background: floorPlanEnabled ? '#64748B' : '#3B82F6',
              color: '#fff', fontWeight: 700, fontSize: 13,
              cursor: floorPlanLoading ? 'not-allowed' : 'pointer',
              opacity: floorPlanLoading ? 0.6 : 1,
            }}
          >
            {floorPlanLoading ? '...' : floorPlanEnabled ? '🙈 Gizlət' : '👁️ Göstər'}
          </button>
        </div>
      )}

      {/* ── BAĞLAMA MODALI ── */}
      {showCloseModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, padding: 32,
            width: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#1a1a2e' }}>
              🔒 Rezervasiyanı Bağla
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748B' }}>
              Hansı müddət üçün bağlamaq istəyirsiniz?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

              <button
                onClick={() => handleCloseBookings(null)}
                style={{
                  padding: '14px 16px', borderRadius: 12,
                  border: '1.5px solid #E5E7EB', background: '#fff',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>♾️ Müddətsiz bağla</div>
                <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>Siz yenidən açana qədər bağlı qalar</div>
              </button>

              <div style={{ border: '1.5px solid #E5E7EB', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e', marginBottom: 10 }}>⏰ Müvəqqəti bağla</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="time"
                    value={timeInputVal}
                    onChange={e => setTimeInputVal(e.target.value)}
                    style={{
                      flex: 1, padding: '9px 12px', borderRadius: 8,
                      border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none',
                    }}
                  />
                  <button
                    onClick={() => {
                      if (!timeInputVal) return;
                      const [h, m] = timeInputVal.split(':');
                      const d = new Date();
                      d.setHours(parseInt(h), parseInt(m), 0, 0);
                      handleCloseBookings(d.toISOString());
                    }}
                    disabled={!timeInputVal}
                    style={{
                      padding: '9px 16px', borderRadius: 8, border: 'none',
                      background: timeInputVal ? '#EF4444' : '#E5E7EB',
                      color: '#fff', fontWeight: 700, fontSize: 13, cursor: timeInputVal ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Tətbiq Et
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>
                  Seçilən saata qədər rezervasiya bağlanacaq
                </div>
              </div>

              <button
                onClick={() => { setShowCloseModal(false); setTimeInputVal(''); }}
                style={{
                  padding: '12px', borderRadius: 12, border: 'none',
                  background: '#F1F5F9', cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#64748B',
                }}
              >
                Ləğv Et
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── BU GÜN ── */}
      <div style={{
        background: 'linear-gradient(135deg, #1A1A2E 0%, #2d2d4e 100%)',
        borderRadius: 18, padding: '20px 24px', marginBottom: 20,
        boxShadow: '0 4px 20px rgba(26,26,46,0.2)',
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.45)', marginBottom: 14, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
          Bu Gün — {new Date().toLocaleDateString('az-AZ', { day: 'numeric', month: 'long' })}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[
            { label: 'Rezervasiya', value: todayStats.reservations, icon: '📅', color: '#667eea' },
            { label: 'Təsdiqlənmiş', value: todayStats.accepted,     icon: '✅', color: '#10b981' },
            { label: 'QR Scan',      value: todayStats.scans,         icon: '📲', color: '#F59E0B' },
          ].map(item => (
            <div key={item.label} style={{
              background: 'rgba(255,255,255,0.07)', borderRadius: 12, padding: '14px 16px',
              border: '1px solid rgba(255,255,255,0.08)',
            }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{item.icon}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{item.value}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── STAT CARDS ── */}
      <div className="stats-grid">
        <StatCard icon={<FaHeart />}      title="İzləyicilər"   value={stats.followers}    color="#FF6B6B" />
        <StatCard icon={<FaCalendarAlt />} title="Gözləyən Rez." value={pendingBookings.length} color="#f093fb" />
        <StatCard icon={<FaQrcode />}     title="Ümumi Scan"    value={stats.scans}         color="#667eea" />
        <StatCard icon={<FaTicketAlt />}  title="İstif. Kupon"  value={stats.couponsUsed}   color="#E63950" />
        <StatCard icon={<FaStar />}       title="Reytinq"       value={stats.rating}        color="#feca57" />
      </div>

      {/* ── GÖZLƏYƏN REZERVASİYALAR ── */}
      {pendingBookings.length > 0 && (
        <div style={{
          background: '#fff', borderRadius: 16, padding: '20px 24px', marginBottom: 24,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: '1.5px solid #fde68a',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: 20 }}>⏳</span>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>Gözləyən Rezervasiyalar</h3>
            <span style={{
              background: '#fef3c7', color: '#d97706', borderRadius: 20,
              padding: '2px 10px', fontSize: 12, fontWeight: 700,
            }}>{pendingBookings.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pendingBookings.map(b => {
              const name    = b.contact_name || b.users?.full_name || 'Müştəri';
              const guests  = b.party_size || b.guest_count;
              const time    = b.reservation_time?.slice(0, 5) || '';
              const date    = b.reservation_date || '';
              const isToday = date === today;
              const busy    = actionLoading === b.id;
              return (
                <div key={b.id} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  background: '#fffbeb', borderRadius: 12, padding: '12px 16px',
                  border: '1px solid #fde68a',
                }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                    background: 'linear-gradient(135deg,#f093fb,#f5576c)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 800, fontSize: 16,
                  }}>
                    {name[0]?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{name}</div>
                    <div style={{ fontSize: 12, color: '#92400e', marginTop: 2 }}>
                      {isToday ? 'Bu gün' : date}{time ? ` · saat ${time}` : ''}{guests ? ` · ${guests} nəfər` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      onClick={() => handleAccept(b)}
                      disabled={busy}
                      style={{
                        padding: '7px 16px', borderRadius: 8, border: 'none',
                        background: busy ? '#d1fae5' : '#10b981', color: '#fff',
                        fontWeight: 700, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer',
                      }}>
                      {busy ? '...' : '✓ Qəbul'}
                    </button>
                    <button
                      onClick={() => handleReject(b)}
                      disabled={busy}
                      style={{
                        padding: '7px 14px', borderRadius: 8, border: 'none',
                        background: busy ? '#fee2e2' : '#ef4444', color: '#fff',
                        fontWeight: 700, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer',
                      }}>
                      {busy ? '...' : '✕'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── İZLƏYİCİLƏR ── */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', marginBottom: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <FaHeart style={{ color: '#FF6B6B' }} />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>İzləyicilər</h3>
          <span style={{ marginLeft: 4, background: '#FFF0F0', color: '#FF6B6B', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
            {followers.length}
          </span>
        </div>
        {followers.length === 0 ? (
          <p style={{ color: '#aaa', fontSize: 13, margin: 0 }}>Hələ heç kim sizi izləməyib.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {followers.map((f, i) => {
              const u = f.users;
              const name = u?.full_name || u?.username || 'İstifadəçi';
              const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
              const branchName = !activeLocation && locations.length > 1 ? f.location?.name : null;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F7F8FF', borderRadius: 30, padding: '6px 12px 6px 6px' }}>
                  {u?.avatar_url ? (
                    <img src={av(u.avatar_url)} alt={name} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700 }}>
                      {initials}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{name}</span>
                    {branchName && <span style={{ fontSize: 11, color: '#667eea', fontWeight: 500 }}>{branchName}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── AKTİVLİK TABLAR ── */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button onClick={() => setTab('activity')} style={{
            flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
            background: tab === 'activity' ? '#1a1a2e' : '#F1F5F9',
            color: tab === 'activity' ? '#fff' : '#64748B',
            fontWeight: 700, fontSize: 14, cursor: 'pointer',
          }}>👤 Scan Tarixçəsi ({recentScans.length})</button>
          <button onClick={() => setTab('coupons')} style={{
            flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
            background: tab === 'coupons' ? '#E63950' : '#F1F5F9',
            color: tab === 'coupons' ? '#fff' : '#64748B',
            fontWeight: 700, fontSize: 14, cursor: 'pointer',
          }}>🎟️ Kupon Tarixçəsi ({recentCoupons.length})</button>
        </div>

        {tab === 'activity' && (
          recentScans.length === 0 ? (
            <p style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Hələ scan edilməyib</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recentScans.map(log => {
                const color = TIER_COLOR[log.amount_tier] || '#888';
                const tierLabel = TIER_LABEL[log.amount_tier] || log.amount_tier;
                return (
                  <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: '#F8F9FF', borderRadius: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>👤</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{log.user_name}</div>
                      <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>{timeAgo(log.created_at)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color, background: color + '18', padding: '3px 10px', borderRadius: 20, marginBottom: 4 }}>{tierLabel}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#F0941F' }}>+{log.points} xal</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {tab === 'coupons' && (
          recentCoupons.length === 0 ? (
            <p style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Hələ kupon oxudulmayıb</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recentCoupons.map(log => (
                <div key={log.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px', background: '#FFF5F5', borderRadius: 12, border: '1px solid #FFE4E4' }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🎟️</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{log.coupon_title}</div>
                    {log.coupon_description && (
                      <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>{log.coupon_description}</div>
                    )}
                    <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
                      👤 {log.user_name} · {timeAgo(log.created_at)}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#10B981', background: '#ECFDF5', padding: '4px 10px', borderRadius: 20, flexShrink: 0 }}>
                    ✓ İstifadə edildi
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
